import clsx from "clsx";
import { AlertTriangle, Info } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";

import { ESCAPE, registerEscape } from "./escapeStack";
import { motion, useClosing } from "./presence";
import { Button } from "./ui";
import { t } from "../i18n/i18n";
import { Waiting } from "./Waiting";

/** Confirmation avant d'agir (R9), comme l'alerte « Confirmation de suppression » de l'iPad : fenêtre au-dessus
 *  de tout (z-90), « Annuler » choisi par défaut (Entrée ne confirme pas par mégarde), Échap pour renoncer (rang le
 *  plus haut de la pile partagée). À entourer de `Presence`.
 *
 *  - `tone` : `danger` (défaut) pour une action sans retour — pastille rouge, bouton `danger` ; `neutral` pour une
 *    action qui s'annule (⌘Z) et ne détruit rien — pastille d'information, bouton principal noir.
 *  - `busy` : action en cours ; les boutons sont désactivés et l'action affiche `busyLabel`, nom de l'action en
 *    cours, traduit (« Suppression… », « Retrait… », « Effacement… », « Régénération… »). Par défaut :
 *    « Suppression… » en `danger` (comme avant ce composant), « En cours… » en `neutral` — jamais le verbe suivi
 *    de « … », qui se lirait comme « ouvre une suite ». Toute action qui n'est pas une suppression passe son
 *    `busyLabel`.
 *  - `extra` : action complémentaire proposée avant de confirmer (« Télécharger d'abord »), à gauche.
 *  - `cancelLabel` : libellé du choix par défaut, s'il dit mieux que « Annuler » ce qu'il fait (« Revenir à la
 *    saisie »). */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  busy = false,
  busyLabel,
  tone = "danger",
  extra,
  cancelLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  busy?: boolean;
  busyLabel?: string;
  tone?: "danger" | "neutral";
  extra?: ReactNode;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const cancel = useRef(onCancel);
  cancel.current = onCancel;
  const closing = useClosing();
  const titleId = useId();
  useEffect(() => {
    // « Annuler » a le focus à l'ouverture : Entrée ne confirme pas par mégarde.
    const previous = document.activeElement as HTMLElement | null;
    cancelRef.current?.focus();
    // Échap : rang le plus haut de la pile partagée, en phase de capture — rien d'autre ne réagit à l'appui
    // (consommé par la pile, propagation arrêtée comme avant).
    const unregister = registerEscape(ESCAPE.modal, (e) => {
      e.stopPropagation();
      cancel.current();
    });
    return () => {
      unregister();
      previous?.focus?.({ preventScroll: true });
    };
  }, []);

  const danger = tone === "danger";
  const Icon = danger ? AlertTriangle : Info;
  return createPortal(
    <div
      className={clsx("fixed inset-0 top-(--gx-strip-h) z-[90] flex items-center justify-center bg-ink/20 p-6", motion("fade", closing))}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy && !closing) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy || undefined}
        className={clsx("w-full max-w-[420px] rounded-xl border border-line bg-surface p-5 shadow-float", motion("zoom", closing))}
      >
        <div className="flex gap-3">
          <span
            className={clsx(
              "mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-full",
              danger ? "bg-danger-soft text-danger" : "bg-accent-soft text-accent",
            )}
          >
            <Icon size={16} />
          </span>
          <div className="min-w-0">
            <h2 id={titleId} className="text-title leading-snug font-semibold text-ink">
              {title}
            </h2>
            <div className="mt-1.5 text-ui leading-relaxed text-ink-soft">{children}</div>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          {extra && <div className="mr-auto">{extra}</div>}
          <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel ?? t("Annuler")}
          </Button>
          <Button variant={danger ? "danger" : "primary"} onClick={onConfirm} disabled={busy}>
            {busy ? <Waiting>{busyLabel ?? (danger ? t("Suppression…") : t("En cours…"))}</Waiting> : confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
